import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import withRouter from "Components/Common/withRouter";
import ConsultantAgreementTemplate, { ConsultantAgreementFormValues } from "Components/templates/hr/document-generator/consultant-agreement-template";
import RejectDocumentFormFields from "Components/organisms/forms/reject-document-form/types";
import { RejectDocumentFormValidation } from "data/reject-document-form";
import { HumanResourcesSliceState } from "slices/human-resources/reducer";
import { DocumentGeneratorState, finishProcessesingDocument, setDocumentTemplateValue, startProcessesingDocument, startViewingDocument, finishViewingDocument, setGenerationError, upsertGeneratedDoc } from "slices/human-resources/document-generator/reducer";
import { getDocument, saveDocumentAsDraft, submitDocument, generateDocument, approveDocument, rejectDocument, rejectDocumentByCandidate, approveOnBehalf as approveOnBehalfFn, getGeneratedDocument } from "slices/human-resources/document-generator/thunk";
import { getFields, getManagers, viewDocument, searchRecruits } from "slices/human-resources/thunk";
import { navigateToGeneratedDocuments } from "utils/navigator";
import { GEN_DOC_STATUS } from "models/gen-doc-status";
import useGeneratedDocPermissions from "hooks/useGeneratedDocPermissions";
import GeneratedDoc from "models/generated-doc";
import Doc from "models/document";
import Employee from "models/employee";
import { PermissionsSliceState } from "slices/auth/permissions/reducer";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import Field from "models/field";
import DocumentGeneratorUtility from "utils/models/document-generator-utility";
import RecruitForm from "Components/organisms/forms/recruit-form";
import RecruitFormFields from "Components/organisms/forms/recruit-form/types";
import { RecruitFormValidation } from "data/recruit-form";
import Recruit from "models/recruit";
import { JobTitle } from "models/org-chart-node";


const buildValidationSchema = (values: ConsultantAgreementFormValues) =>
    Yup.object({
        companyRepresentative: Yup.string().required("Company Representative is required"),
        consultantRepresentative: Yup.string().required("Consultant Representative is required"),
        termEndDate: Yup.string().required("Term End Date is required"),
        duties: Yup.string().required("Duties are required"),
        location: Yup.string().required("Work Location is required"),
        siteAddress: Yup.string().when("location", {
            is: "Site",
            then: (s) => s.required("Site Address is required when location is Site"),
            otherwise: (s) => s.optional(),
        }),
        schedule: Yup.string().required("Schedule is required"),
        descriptionTitle: Yup.string().required("Description Title is required"),
        hourlyRate: Yup.number().typeError("Must be a number").required("Hourly Rate is required").min(0),
        vehicleReimbursement: Yup.string().required("Vehicle Reimbursement is required"),
        reimbursementType: Yup.string().when("vehicleReimbursement", {
            is: "Yes",
            then: (s) => s.required("Reimbursement Type is required"),
            otherwise: (s) => s.optional(),
        }),
        vehicleRate: Yup.number().when("reimbursementType", {
            is: "Vehicle",
            then: (s) => s.typeError("Must be a number").required("Vehicle Rate is required").min(0),
            otherwise: (s) => s.optional(),
        }),
        loaRate: Yup.number().when("loaApplicable", {
            is: "Yes",
            then: (s) => s.typeError("Must be a number").required("LOA Rate is required").min(0),
            otherwise: (s) => s.optional(),
        }),
        travelRequired: Yup.string().required("Travel Required is required"),
    });

const selectHR = createSelector(
    (state: any) => state.HumanResources,
    ({ managers, jobTitles, designations }: HumanResourcesSliceState) => ({ managers, jobTitles, designations })
);

const selectDocGen = createSelector(
    (state: any) => state.DocumentGenerator,
    ({ doc, processingDocument, viewingDocument, generateDocError, savedValues, docFields }: DocumentGeneratorState) => ({
        doc, processingDocument, viewingDocument, generateDocError, savedValues, docFields
    })
);

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
);

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({ employee })
);

const ConsultantAgreementPage = (props: any) => {
    document.title = "Consultant Agreement | IbexIQ";
    const dispatch: any = useDispatch();
    const [searchParams] = useSearchParams();

    const [genDocStatus, setGenDocStatus] = useState<GEN_DOC_STATUS | null>(null);
    const [generatedDoc, setGeneratedDoc] = useState<GeneratedDoc | null>(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showCandidateRejectModal, setShowCandidateRejectModal] = useState(false);
    const [showApproveOnBehalf, setShowApproveOnBehalf] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState<boolean>(false);
    const [recruitSearchQuery, setRecruitSearchQuery] = useState<string>("");
    const [recruitSearchResults, setRecruitSearchResults] = useState<Recruit[]>([]);
    const [searchingRecruits, setSearchingRecruits] = useState<boolean>(false);
    const intentRef = useRef<"draft" | "submit">("draft");

    const { managers, jobTitles, designations } = useSelector(selectHR);
    const { doc, processingDocument, viewingDocument, generateDocError, savedValues, docFields } = useSelector(selectDocGen);
    const { screens } = useSelector(selectPermissions);
    const { employee } = useSelector(selectProfile);

    const initialValues = useMemo(() => {
        if (docFields.length === 0 || Object.keys(savedValues).length === 0) {
            return {
                companyRepresentative: '', consultantRepresentative: '', termEndDate: '', duties: '',
                certifications: [], location: '', siteAddress: '', schedule: '', descriptionTitle: '',
                hourlyRate: '', maxDailyRate: '', vehicleReimbursement: '', reimbursementType: '',
                vehicleRate: '', loaApplicable: '', loaRate: '', travelRequired: '', otherExpenses: ''
            }
        }
        const res = {};
        docFields.forEach((v: Field) => res[v.name] = savedValues[v.name] ?? "")
        return res;
    }, [docFields, savedValues])

    const recruitJobTitleOptions = useMemo(() => {
        return [
            { value: '', label: 'Select Position' },
            ...jobTitles.map((jobTitle: JobTitle) => ({ value: jobTitle.id, label: jobTitle.description }))
        ];
    }, [jobTitles])

    const recruitManagerOptions = useMemo(() =>
        DocumentGeneratorUtility.employeeOptions(managers, undefined, 'Select Hiring Manager')
    , [managers])



    const {
        isHRAdmin,
        submittable,
        draftable,
        downloadable,
        editable,
        approvable,
        rejectable,
        candidateRejectable,
        approveOnBehalf,
        approvalBlockedReason
    } = useGeneratedDocPermissions(generatedDoc, genDocStatus)

    const approvers: Employee[] = useMemo(() => {
        if (!generatedDoc) return [];
        const res: Employee[] = [];
        if (generatedDoc.managerAppr && !generatedDoc.managerApprovedAt) res.push(generatedDoc.managerAppr);
        if (generatedDoc.higherMgrAppr && !generatedDoc.higherManagerApprovedAt) res.push(generatedDoc.higherMgrAppr);
        return res;
    }, [generatedDoc]);

    const initializePage = async (documentId: string, generatedDocId: string | null) => {
        const [loadedDoc, , loadedGenDoc, loadedFields] = await Promise.all([
            dispatch(getDocument(documentId)),
            dispatch(getManagers()),
            generatedDocId ? getGeneratedDocument(generatedDocId) : Promise.resolve(undefined),
            dispatch(getFields())
        ]);

        if (!loadedDoc) {
            toast.error("Could not load document");
            props.router.navigate(-1);
            return;
        }

        if (loadedGenDoc) {
            setGeneratedDoc(loadedGenDoc);
            if (loadedGenDoc.status) setGenDocStatus(loadedGenDoc.status.description as GEN_DOC_STATUS);

            const saved: Partial<ConsultantAgreementFormValues> = {};
            for (const f of loadedGenDoc.fields ?? []) {
                if (!f.field?.name) continue;
                if (f.field.name === "certifications") {
                    try { (saved as any).certifications = JSON.parse(f.value ?? "[]"); }
                    catch { (saved as any).certifications = []; }
                } else {
                    (saved as any)[f.field.name] = f.value ?? "";
                }
            }
            dispatch(setDocumentTemplateValue(saved));

            const genDocRecruit = loadedGenDoc.genDocRecruits?.[0];
            if (genDocRecruit) {

                recruitFormik.setValues({
                    firstName: genDocRecruit.firstName,
                    middleName: genDocRecruit.middleName ?? '',
                    lastName: genDocRecruit.lastName,
                    commonName: genDocRecruit.commonName ?? '',
                    email: genDocRecruit.email,
                    phone: genDocRecruit.phone ?? '',
                    address: genDocRecruit.address ?? '',
                    jobTitleId: loadedFields?.jobTitles.find(j => j.description === genDocRecruit.jobTitle)?.id ?? '',
                    managerId: loadedGenDoc.managerApproverId ?? ''
                });

            }
        }
    };

    useEffect(() => {
        const docId = searchParams.get("docId");
        const genDocId = searchParams.get("genDocId");
        if (!docId) {
            toast.error("Document ID not found");
            navigateToGeneratedDocuments(props.router.navigate);
            return;
        }
        initializePage(docId, genDocId);

        return () => dispatch(setDocumentTemplateValue({}))
    }, []);

    const caFormik = useFormik<ConsultantAgreementFormValues>({
        initialValues: initialValues as ConsultantAgreementFormValues,
        validationSchema: Yup.lazy(() => buildValidationSchema(caFormik.values)),
        enableReinitialize: true,
        onSubmit: () => { },
    });

    const recruitFormik = useFormik<RecruitFormFields>({
        initialValues: {
            firstName: '', middleName: '', lastName: '', commonName: '',
            email: '', phone: '', address: '', jobTitleId: '', managerId: ''
        },
        validationSchema: RecruitFormValidation,
        onSubmit: () => { }
    })

    const performSubmit = useCallback(async () => {
        if (!doc) return;
        dispatch(startProcessesingDocument());
        const genDocId = searchParams.get("genDocId");

        const serialized = {
            ...caFormik.values,
            certifications: JSON.stringify(caFormik.values.certifications),
        };

        let res: GeneratedDoc | null = null;
        if (intentRef.current === "draft") {
            res = await dispatch(saveDocumentAsDraft(doc, recruitFormik.values, serialized, genDocId));
        } else {
            res = await dispatch(submitDocument(doc, recruitFormik.values, serialized, genDocId));
        }

        if (res) {
            props.router.navigate(-1);
            dispatch(upsertGeneratedDoc({ ...generatedDoc, document: doc, ...res }));
        }
        dispatch(finishProcessesingDocument(""));
    }, [doc, recruitFormik.values, caFormik.values, searchParams, generatedDoc]);

    const handleSubmitWithIntent = useCallback(async (intent: 'draft' | 'submit') => {
        intentRef.current = intent;

        // Recalling a Pending doc back to Draft is a pure status change -- nothing is being
        // edited, so there's nothing to validate.
        if (intent === 'draft' && genDocStatus === GEN_DOC_STATUS.PENDING) {
            await performSubmit();
            return;
        }

        await recruitFormik.submitForm();
        const recruitErrors = await recruitFormik.validateForm();
        await caFormik.submitForm();
        const caErrors = await caFormik.validateForm();

        if (Object.keys(recruitErrors).length > 0 || Object.keys(caErrors).length > 0) return;

        await performSubmit();
    }, [genDocStatus, recruitFormik.submitForm, recruitFormik.validateForm, caFormik.submitForm, caFormik.validateForm, performSubmit]);

    const handleSearchRecruits = useCallback(async () => {
        if (!recruitSearchQuery.trim()) return;
        setSearchingRecruits(true);
        const results = await searchRecruits(recruitSearchQuery.trim());
        setRecruitSearchResults(results);
        setSearchingRecruits(false);
    }, [recruitSearchQuery])

    const handleSelectRecruit = useCallback((recruit: Recruit) => {
        recruitFormik.setValues({
            firstName: recruit.firstName,
            middleName: recruit.middleName ?? '',
            lastName: recruit.lastName,
            commonName: recruit.commonName ?? '',
            email: recruit.email,
            phone: recruit.phone ?? '',
            address: recruit.address ?? '',
            jobTitleId: recruit.jobTitleId,
            managerId: recruit.managerId ?? ''
        });
        setRecruitSearchResults([]);
    }, [recruitFormik.setValues])

    const rejectFormik = useFormik({
        initialValues: { reason: "" } as RejectDocumentFormFields,
        validationSchema: RejectDocumentFormValidation,
        onSubmit: async (values: RejectDocumentFormFields, helpers: FormikHelpers<RejectDocumentFormFields>) => {
            if (!generatedDoc) return;
            const res = await rejectDocument(values.reason, generatedDoc.id);
            if (res) {
                handleBack();
                dispatch(upsertGeneratedDoc({ ...generatedDoc, ...res }));
            }
        },
    });

    const candidateRejectFormik = useFormik({
        initialValues: { reason: "" } as RejectDocumentFormFields,
        validationSchema: RejectDocumentFormValidation,
        onSubmit: async (values: RejectDocumentFormFields, helpers: FormikHelpers<RejectDocumentFormFields>) => {
            if (!generatedDoc) return;
            const res = await rejectDocumentByCandidate(values.reason, generatedDoc.id);
            if (res) {
                handleBack();
                dispatch(upsertGeneratedDoc({ ...generatedDoc, ...res }));
            }
        },
    });

    const handleBack = useCallback(() => props.router.navigate(-1), [props.router.navigate]);

    const handleViewDocument = useCallback(async (document: Doc) => {
        dispatch(startViewingDocument());
        const success = await viewDocument(document.id, generatedDoc?.id);
        if (!success) {
            dispatch(setGenerationError("Could not load document preview."));
        }
        dispatch(finishViewingDocument());
    }, [generatedDoc]);

    const handleDraftDocument = useCallback(() => {
        handleSubmitWithIntent('draft');
    }, [handleSubmitWithIntent]);

    const handleSubmitDocument = useCallback(() => {
        handleSubmitWithIntent('submit');
    }, [handleSubmitWithIntent]);

    const handleDownloadDocument = useCallback(async () => {
        const genDocId = searchParams.get("genDocId");
        if (!genDocId) return;
        await dispatch(generateDocument(genDocId));
    }, [searchParams]);

    const handleShowApproveModal = useCallback(() => {
        setShowApproveModal(true)
    }, [setShowApproveModal])

    const handleHideApprovefModal = useCallback(() => {
        setShowApproveModal(false)
    }, [setShowApproveModal])

    const handleApproveDocument = useCallback(async () => {
        if (!generatedDoc) return;
        dispatch(startProcessesingDocument());
        setShowApproveModal(false)
        try {
            const res = await approveDocument(generatedDoc.id);
            if (res) {
                handleBack();
                dispatch(upsertGeneratedDoc({ ...generatedDoc, document: doc ?? undefined, ...res }));
            }
        } finally {
            dispatch(finishProcessesingDocument(""));
        }
    }, [generatedDoc, doc, handleBack]);

    const handleApproveOnBehalf = useCallback(async (employees: string[]) => {
        if (!generatedDoc) return;
        dispatch(startProcessesingDocument());
        try {
            const res = await approveOnBehalfFn(generatedDoc.id, employees);
            if (res) {
                dispatch(upsertGeneratedDoc({ ...generatedDoc, document: doc ?? undefined, ...res }));
                handleBack();
            }
        } finally {
            dispatch(finishProcessesingDocument(""));
        }
    }, [generatedDoc, doc, handleBack]);

    return (
        <ConsultantAgreementTemplate
            doc={doc ?? undefined}
            processingDocument={processingDocument}
            viewingDocument={viewingDocument}
            showRejectModal={showRejectModal}
            showApproveOnBehalf={showApproveOnBehalf}
            showApproveModal={showApproveModal}
            generateDocError={generateDocError}
            docStatus={genDocStatus ?? undefined}
            approvers={approvers}
            approvalBlockedReason={approvalBlockedReason}
            editable={editable}
            caValues={caFormik.values}
            caErrors={caFormik.errors}
            caTouched={caFormik.touched}
            caHandleChange={caFormik.handleChange}
            caHandleBlur={caFormik.handleBlur}
            caSetFieldValue={caFormik.setFieldValue}
            companyRepresentatives={DocumentGeneratorUtility.employeeOptions(managers, generatedDoc?.companyRepresentative, "Select Employee")}
            recruitFormSection={<RecruitForm
                values={recruitFormik.values}
                errors={recruitFormik.errors}
                touched={recruitFormik.touched}
                handleChange={recruitFormik.handleChange}
                handleBlur={recruitFormik.handleBlur}
                handleSubmit={recruitFormik.handleSubmit}
                isValid={recruitFormik.isValid}
                isSubmitting={recruitFormik.isSubmitting}
                jobTitles={recruitJobTitleOptions}
                managers={recruitManagerOptions}
                searchQuery={recruitSearchQuery}
                onSearchQueryChange={setRecruitSearchQuery}
                onSearch={handleSearchRecruits}
                searching={searchingRecruits}
                searchResults={recruitSearchResults}
                onSelectRecruit={handleSelectRecruit}
                editable={editable}
            />}
            jobTitles={[
                { value: "", label: "Select Job Title" },
                ...jobTitles.map(j => ({ value: j.description, label: j.description }))
            ]}
            certifications={[...designations.map(d => ({ label: d.description, value: d.description }))]}
            handleBack={handleBack}
            handleViewDocument={handleViewDocument}
            handleDraftDocument={draftable ? handleDraftDocument : undefined}
            handleSubmitDocument={submittable ? handleSubmitDocument : undefined}
            handleDownloadDocument={downloadable ? handleDownloadDocument : undefined}
            handleApproveDocument={approvable ? handleApproveDocument : undefined}
            handleShowRejectModal={rejectable ? () => setShowRejectModal(true) : undefined}
            handleHideRejectModal={rejectable ? () => { setShowRejectModal(false); rejectFormik.resetForm(); } : undefined}
            handleShowCandidateRejectModal={candidateRejectable ? () => setShowCandidateRejectModal(true) : undefined}
            handleHideCandidateRejectModal={candidateRejectable ? () => { setShowCandidateRejectModal(false); candidateRejectFormik.resetForm(); } : undefined}
            showCandidateRejectModal={showCandidateRejectModal}
            candidateRejectForm={{
                values: candidateRejectFormik.values,
                errors: candidateRejectFormik.errors,
                touched: candidateRejectFormik.touched,
                handleChange: candidateRejectFormik.handleChange,
                handleBlur: candidateRejectFormik.handleBlur,
                handleSubmit: candidateRejectFormik.handleSubmit,
                isValid: candidateRejectFormik.isValid,
                isSubmitting: candidateRejectFormik.isSubmitting,
            }}
            handleShowApproveOnBehalf={approveOnBehalf ? () => setShowApproveOnBehalf(true) : undefined}
            handleHideApproveOnBehalf={() => setShowApproveOnBehalf(false)}
            handleApproveOnBehalf={approveOnBehalf ? handleApproveOnBehalf : undefined}
            handleHideApproveModal={approvable ? handleHideApprovefModal : undefined}
            handleShowApproveModal={approvable ? handleShowApproveModal : undefined}
            values={rejectFormik.values}
            errors={rejectFormik.errors}
            touched={rejectFormik.touched}
            handleChange={rejectFormik.handleChange}
            handleBlur={rejectFormik.handleBlur}
            handleSubmit={rejectFormik.handleSubmit}
            isValid={rejectFormik.isValid}
            isSubmitting={rejectFormik.isSubmitting}
        />
    );
};

export default withRouter(ConsultantAgreementPage);
